import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  stack: string | null;
}

/**
 * Without this, a render-time exception unmounts the whole React tree and the
 * page goes blank with nothing in the UI to explain why. This keeps the failure
 * on screen and readable, which matters most on a route the visitor reached by
 * navigation rather than a fresh load.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, stack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[render error]', error, info.componentStack);
    this.setState({ stack: info.componentStack ?? null });
  }

  override render(): ReactNode {
    const { error, stack } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="grid min-h-screen place-items-center bg-canvas px-5 py-20">
        <div className="w-full max-w-2xl">
          <p className="eyebrow mb-4">Error</p>
          <h1 className="h3 mb-4">Algo se rompió al dibujar esta página</h1>

          <p className="mb-6 text-sm text-muted">
            El detalle está abajo y en la consola del navegador. Recargar suele devolverte al sitio.
          </p>

          <pre className="mb-6 max-h-64 overflow-auto rounded-xl border border-line bg-elevated p-4 text-xs leading-relaxed text-muted">
            {error.message}
            {stack ? `\n${stack}` : ''}
          </pre>

          <div className="flex flex-wrap gap-3">
            <button type="button" onClick={() => window.location.reload()} className="btn-primary">
              Recargar
            </button>
            <a href="/" className="btn-outline">
              Ir al inicio
            </a>
          </div>
        </div>
      </div>
    );
  }
}
